'use client';

import type { CSSProperties, PropsWithChildren } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { mergeProps, useId } from 'react-aria';
import clsx from 'clsx';
import { useHideBody } from 'components/Dialog/useHideBody';
import { Portal } from 'components/Portal';
import { motion, useReducedMotion } from 'framer-motion';
import { useBodyScrollLock } from 'hooks/useBodyScrollLock';
import { useFocusTrap } from 'hooks/useFocusTrap';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { isComposingEvent } from 'utils/is-composing';
import { noop } from 'utils/noop';
import { useFocus, useInteractionModality } from '@react-aria/interactions';
import type { IconProps } from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';
import styles from './Sheet.module.css';
import { SheetContext } from './SheetContext';
import { SheetHead } from './SheetHead';
import type { SheetPlacements } from './types';

export type SheetContentProps = PropsWithChildren<{
  /** A unique identifier for the panel, used for animations and accessibility. eg. 'notifications-sheet' */
  id?: string;
  /** @deprecated Use `SheetHead` as a child of `Sheet` instead. */
  title?: string;
  /** A callback to indicate that the sheet should close. */
  onRequestClose: () => void;
  /** An offset position from the side of the viewport. */
  offset?: number;
  /** Where relative to the viewport does it appear? Currently only 'left' is supported. */
  placement?: SheetPlacements;
  /** If a portal is disabled, the element is positioned within its container. */
  shouldUsePortal?: boolean;
  /**
   * When disabled, elements outside of the Sheet will remain interactive and the Sheet
   * won't automatically close upon clicking outside of it.
   */
  shouldUseOverlay?: boolean;
  /** The desired size of the panel. On mobile devices, this will be overridden to 100vw */
  size?: number;
  /** A margin which appears around sheet. */
  margin?: number;
  /**
   * An optional radius. Note that you can customize corners using the
   * border-radius shorthand.
   */
  radius?: string;
  /**
   * An optional label for the Collapse button. VoiceOver will read "{collapseButtonLabel}, Button. {aria-label/title prop}, Group."
   */
  collapseButtonLabel?: string;
  /**
   * Styles applied specifically to the offset container that wraps the sheet.
   */
  offsetContainerStyles?: CSSProperties;
  /**
   * Option to pass in a custom icon.
   */
  closeIcon?: React.ReactElement<IconProps>;
  /**
   * Optionally close the sheet when clicking outside of it.
   */
  closeOnClickOutside?: boolean;
  /**
   * Optionally delay the show animation (in seconds)
   */
  transitionDelayOpen?: number;
  /**
   * Optionally delay the hide animation (in seconds)
   */
  transitionDelayClose?: number;
}> &
  React.HTMLAttributes<HTMLDivElement>;

function SheetContent({
  onRequestClose,
  placement = 'left',
  offset = 0,
  children,
  title,
  id: idProp,
  shouldUseOverlay = true,
  shouldUsePortal = true,
  size = 400,
  margin = 2,
  radius = `${theme.radii[8]}`,
  collapseButtonLabel = 'Collapse',
  offsetContainerStyles,
  closeOnClickOutside = false,
  closeIcon,
  transitionDelayOpen = 0,
  transitionDelayClose = 0,
  className,
  ...other
}: SheetContentProps) {
  const id = useId(idProp);
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const initialFocusRef = useRef<HTMLElement>(null);

  const modality = useInteractionModality();
  const [isFocused, setIsFocused] = useState(false);

  const { focusProps } = useFocus({
    onFocusChange: setIsFocused,
  });

  // trap focus inside our sheet when the overlay is enabled
  const handleTab = useFocusTrap(ref);

  // lock body scrolling when using "Portal" mode
  useBodyScrollLock({ enabled: shouldUsePortal });

  // set aria-hidden to all elements besides this one when visible
  useHideBody(containerRef, shouldUseOverlay);

  // if enabled, close sheet when clicking outside of it unless
  // the clicked element is meant to trigger this sheet
  const { bind: bindOnClickOutside } = useOnClickOutside(
    (e: MouseEvent) => {
      if (!(e.target instanceof Element)) return;

      if (
        e.target.getAttribute('aria-controls') === id ||
        e.target.closest(`[aria-controls='${id}']`)
      ) {
        return;
      }

      onRequestClose();
    },
    { enabled: closeOnClickOutside }
  );

  // Close the sheet when escape is pressed
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isComposingEvent(e)) return;
      // Trap focus when the overlay is enabled
      if (shouldUseOverlay) {
        handleTab(e);
      }

      if (e.key === 'Escape') {
        // if we have a sheet within a sheet (plz no), only close
        // the first one.
        e.stopPropagation();
        onRequestClose();
      }
    },
    [shouldUseOverlay, handleTab, onRequestClose]
  );

  // focus the initial focus element upon mount, and return
  // focus to the origin focus target upon unmount
  useEffect(() => {
    let originFocusTarget: HTMLElement | null = null;
    // Run within a raf to account for buttons that trigger
    // the sheet on mouseDown. This ensures that the button
    // will receive focus when pressed before we record
    // the activeElement of the document.
    const raf = requestAnimationFrame(() => {
      originFocusTarget = document.activeElement as HTMLElement;
      const focusTarget = initialFocusRef?.current || ref.current;
      focusTarget?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      if (originFocusTarget && originFocusTarget.focus) {
        originFocusTarget.focus();
      }
    };
  }, []);

  const sheetContext = useMemo(() => {
    return {
      onRequestClose,
      isShowing: true,
      id,
      size,
      initialFocusRef,
      placement,
    };
  }, [id, onRequestClose, size, placement]);

  const reducedMotion = useReducedMotion();

  const cssVars = {
    '--vapor-sheet-radius': radius,
    '--vapor-sheet-offset': `${offset}px`,
    '--vapor-sheet-margin': `${margin}px`,
    '--vapor-sheet-position': shouldUsePortal ? 'fixed' : 'absolute',
    '--vapor-sheet-size': `${size}px`,
    '--vapor-sheet-combined-offset': `${offset + margin * 2}px`,
  } as CSSProperties;

  return (
    <Portal disabled={!shouldUsePortal}>
      <div
        ref={containerRef}
        className={styles.offsetContainer}
        data-placement={placement}
        data-testid="sheet-container"
        style={{
          ...offsetContainerStyles,
          ...cssVars,
        }}
      >
        {shouldUseOverlay && (
          <div
            aria-hidden
            className={styles.overlay}
            data-testid="overlay"
            onClick={onRequestClose}
            onDragEnter={onRequestClose}
          />
        )}
        <motion.div
          ref={ref}
          animate={'show'}
          aria-labelledby={other['aria-label'] ?? id}
          className={clsx(styles.styledSheet, className)}
          data-focus-visible={
            (isFocused && modality === 'keyboard') || undefined
          }
          exit={'hidden'}
          initial={'hidden'}
          // Opts opacity out of WAAPI. framer-motion < 12.23 cancels the
          // finished WAAPI animation before committing the final style,
          // which Chrome can paint as a one-frame flicker.
          onUpdate={noop}
          tabIndex={-1}
          variants={animations(
            placement,
            Boolean(reducedMotion),
            transitionDelayOpen,
            transitionDelayClose
          )}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...(mergeProps(other, bindOnClickOutside, focusProps, {
            onKeyDown,
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
          }) as any)}
        >
          <SheetContext.Provider value={sheetContext}>
            <div className={styles.sheetContentContainer}>
              {title && (
                <SheetHead
                  closeIcon={closeIcon}
                  collapseButtonLabel={collapseButtonLabel}
                  title={title}
                />
              )}
              {children}
            </div>
          </SheetContext.Provider>
        </motion.div>
      </div>
    </Portal>
  );
}

function animations(
  placement: SheetPlacements,
  reducedMotion = false,
  delayOpen = 0,
  delayClose = 0
) {
  const variants = (
    hide: { x: string } | { y: string },
    show: { x: number } | { y: number }
  ) =>
    ({
      show: {
        opacity: 1,
        ...(reducedMotion ? {} : show),
        transition: {
          duration: 0.25,
          ease: 'easeOut',
          delay: delayOpen,
        },
      },
      hidden: {
        opacity: 0,
        ...(reducedMotion ? {} : hide),
        transition: {
          duration: 0.15,
          ease: 'easeIn',
          delay: delayClose,
        },
      },
    } as const);

  switch (placement) {
    case 'left':
      return variants({ x: '-100%' }, { x: 0 });
    case 'right':
      return variants({ x: '100%' }, { x: 0 });
    case 'bottom':
      return variants({ y: '100%' }, { y: 0 });
    case 'top':
      return variants({ y: '-100%' }, { y: 0 });
    default:
      throw new Error(`Invalid placement: ${placement}`);
  }
}

const MemoizedSheet = React.memo(SheetContent);

export { MemoizedSheet as SheetContent };
